/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { FC } from 'react';
import classNames from 'classnames';
import { APP_HEX } from '@/constant/theme/colors.constant';
import { useTheme } from '@/context/theme.context';
import { Text } from './AppText';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToggleSize = 'xs' | 'sm' | 'md' | 'lg';

export type ToggleColor =
  | 'primary'
  | 'blue'
  | 'purple'
  | 'green'
  | 'red'
  | 'orange'
  | 'teal'
  | 'indigo'
  | 'emerald'
  | 'amber'
  | 'gray';

export interface AppToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: ToggleSize;
  color?: ToggleColor;
  disabled?: boolean;
  className?: string;
  label?: string;
  labelPosition?: 'left' | 'right';
  testId?: string;
}

// ─── Size maps ────────────────────────────────────────────────────────────────

const TRACK: Record<ToggleSize, { w: number; h: number }> = {
  xs: { w: 28, h: 16 },
  sm: { w: 36, h: 20 },
  md: { w: 44, h: 24 },
  lg: { w: 56, h: 30 },
};

const THUMB: Record<ToggleSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 26,
};

const LABEL_SIZE: Record<ToggleSize, 'sm' | 'md' | 'base' | 'lg'> = {
  xs: 'sm',
  sm: 'md',
  md: 'base',
  lg: 'lg',
};

// ─── Color map — uses APP_HEX for non-primary ─────────────────────────────────

const COLOR_HEX: Record<Exclude<ToggleColor, 'primary'>, string> = {
  blue: APP_HEX.blue,
  purple: APP_HEX.purple,
  green: APP_HEX.green,
  red: APP_HEX.danger,
  orange: APP_HEX.orange,
  teal: APP_HEX.teal,
  indigo: APP_HEX.info,
  emerald: APP_HEX.success,
  amber: APP_HEX.warning,
  gray: APP_HEX.muted,
};

// ─── Component ────────────────────────────────────────────────────────────────

const AppToggle: FC<AppToggleProps> = ({
  checked,
  onChange,
  size = 'md',
  color = 'primary',
  disabled = false,
  className,
  label,
  labelPosition = 'right',
  testId
}) => {
  const { colors } = useTheme();

  const activeColor =
    color === 'primary'
      ? colors.primary
      : COLOR_HEX[color as Exclude<ToggleColor, 'primary'>];

  const track = TRACK[size];
  const thumbDia = THUMB[size];
  const padding = 2;
  const travel = track.w - thumbDia - padding * 2;

  const trackStyle: React.CSSProperties = {
    width: track.w,
    height: track.h,
    borderRadius: track.h,
    padding,
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
    backgroundColor: checked ? activeColor : undefined,
    boxShadow: checked ? `0 0 0 3px ${activeColor}28` : undefined,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    flexShrink: 0,
    outline: 'none',
    border: 'none',
  };

  const thumbStyle: React.CSSProperties = {
    width: thumbDia,
    height: thumbDia,
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.28), 0 0 1px rgba(0,0,0,0.12)',
    transform: checked ? `translateX(${travel}px)` : 'translateX(0px)',
    transition: 'transform 0.25s cubic-bezier(0.34,1.2,0.64,1)',
    flexShrink: 0,
  };

  const switchEl = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={trackStyle}
      className={classNames(
        'toggle-track focus-visible:ring-2 focus-visible:ring-offset-2',
        !checked && 'bg-gray-200 dark:bg-gray-600',
        className,
      )}
      data-testid={`${testId ? `${testId}-switch-btn` : ""}`}
    >
      <span style={thumbStyle} />
    </button>
  );

  if (!label) return switchEl;

  return (
    <label
      className={classNames(
        'inline-flex max-w-full flex-nowrap items-center gap-2 select-none whitespace-nowrap',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      {labelPosition === 'left' && (
        <Text
          size={LABEL_SIZE[size]}
          weight="medium"
          color="muted"
          className="leading-none min-w-0 break-words"
        >
          {label}
        </Text>
      )}
      {switchEl}
      {labelPosition === 'right' && (
        <Text
          size={LABEL_SIZE[size]}
          weight="medium"
          color="muted"
          className="leading-none min-w-0 break-words"
        >
          {label}
        </Text>
      )}
    </label>
  );
};

export default AppToggle;
